import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../lib/api.ts';

interface Props {
  eyebrow: string;
  title: string;
  intro?: ReactNode;
  /** Absent while a page is still deciding what to show. */
  children?: ReactNode;
}

/**
 * The frame every auth page shares - the same centred single-column card the
 * operator sign-in uses, so the two feel like one app rather than two.
 */
export default function AuthShell({ eyebrow, title, intro, children }: Props) {
  return (
    <main className="mx-auto grid w-full max-w-md flex-1 place-items-center px-6 py-12">
      <div className="grid w-full gap-6">
        <Link to="/" className="eyebrow hover:text-petal">
          Wallfeed
        </Link>
        <div className="panel grid w-full gap-5 p-6 sm:p-8">
          <div>
            <p className="eyebrow">{eyebrow}</p>
            <h1 className="font-display mt-2 text-2xl font-semibold">{title}</h1>
            {intro ? <p className="mt-2 text-sm text-muted">{intro}</p> : null}
          </div>
          {children}
        </div>
      </div>
    </main>
  );
}

/** One field, laid out the way every other field in the app is. */
export function Field({
  id,
  label,
  hint,
  ...input
}: {
  id: string;
  label: string;
  hint?: string;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className="grid gap-2">
      <label className="eyebrow" htmlFor={id}>
        {label}
      </label>
      <input id={id} className="field" {...input} />
      {hint ? <p className="text-xs text-muted">{hint}</p> : null}
    </div>
  );
}

/**
 * Sign-in and the two "email me a link" routes are rate limited hard, so how long
 * to wait is the only useful thing to say about a 429.
 */
export function describeAuthError(err: unknown): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : 'Something went wrong.';
  }
  if (err.status === 401) return 'That email and password do not match.';
  if (err.status === 403) return err.message;
  if (err.status === 429) {
    const seconds = err.retryAfterSeconds ?? 0;
    const wait =
      seconds >= 90
        ? `about ${Math.round(seconds / 60)} minutes`
        : seconds > 0
          ? `${seconds} seconds`
          : 'a few minutes';
    return `Too many attempts. Try again in ${wait}.`;
  }
  return err.message;
}
